import { fetchSignedUrlApi } from "@/modules/irohana-components/api/services/global/file";
import { API_UNKNOWN_ERROR_MESSAGE } from "@/modules/irohana-components/constants/common";
import { FILE_EXISTED } from "@/modules/irohana-components/constants/file";
import { showError } from "@/modules/irohana-components/utils/error";
import { getFileNameFromPath } from "@/modules/irohana-components/utils/file";
import axios from "axios";
import iconv from "iconv-lite";
import { toast } from "react-toastify";

/**
 * convert CSV Data To Download Url With UTF8
 *
 * @param {string} csvData - The csv data to encode to UTF8
 */
export const convertCSVDataToDownloadUrlWithUTF8 = (csvData: string) => {
  // Create a data URL with UTF-8 encoded CSV
  const downloadUrl = `data:text/csv;charset=utf-8,${encodeURIComponent(csvData)}`;
  return downloadUrl;
};

/**
 * encode JSON data to shift-JIS
 *
 * @param {string} csvData - The csv data to encode to shift-JIS.
 */
export const convertCSVDataToDownloadUrlWithShiftJIS = (csvData: string) => {
  // Encode the CSV content to Shift-JIS
  const csvShiftJIS = iconv.encode(csvData, "Shift_JIS");
  // Convert Shift-JIS content to base64
  const base64Csv = btoa(new Uint8Array(csvShiftJIS).reduce((data, byte) => data + String.fromCharCode(byte), ""));

  // Create a data URL with base64 encoded CSV
  const downloadUrl = `data:text/csv;charset=Shift_JIS;base64,${base64Csv}`;
  return downloadUrl;
};

/**
 * encode JSON data to UTF16Csv
 *
 * @param {string} csvData - The csv data to encode to UTF16Csv.
 */
export const convertCSVDataToDownloadUrlWithUTF16LE = (csvData: string) => {
  // Encode the CSV content to UTF-16LE
  const csvWithBom = "\uFEFF" + csvData;
  const utf16Csv = iconv.encode(csvWithBom, "utf16le");

  const blob = new Blob([utf16Csv as unknown as BlobPart], { type: "text/csv;charset=utf-16le;" });
  const downloadUrl = URL.createObjectURL(blob);

  return downloadUrl;
};

/**
 * Downloads a file from a given URL.
 *
 * @param {string} fileName - The name of the file to download.
 * @param {string} downloadUrl - The URL to download the file from.
 */
export const downloadFileByUrl = async (fileName: string, downloadUrl: string) => {
  try {
    const res = await axios.get(downloadUrl, { responseType: "blob" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(res.data);
    link.download = fileName;
    link.click();
    URL.revokeObjectURL(link.href);
  } catch (error) {
    if (axios.isAxiosError(error) && (error?.status === 404 || error?.response?.status === 404)) {
      showError({ message: "ファイルが見つかりません" });
      return;
    } else {
      showError({ message: API_UNKNOWN_ERROR_MESSAGE });
    }
  }
};

export const downloadFile = async (fileName: string, file: File) => {
  const arrayBuffer = await file.arrayBuffer();
  const blob = new Blob([arrayBuffer]);
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(link.href);
};

/**
 * Fetches a signed URL for a file and initiates its download.
 *
 * @param {string} filePath - The path of the file to download.
 * @param {string} options.aliasFileName - The name of the file after downloaded.
 * @param {string} options.size - The specific size of the file to download.
 */
export const downloadFileFromPath = async (filePath: string, options?: { aliasFileName?: string; size: "300x400" }) => {
  const rs = await fetchSignedUrlApi(filePath, options?.size);
  const url = rs.data.signedUrl;
  const fileName = options?.aliasFileName ?? getFileNameFromPath(filePath);
  if (!fileName) {
    toast.error(API_UNKNOWN_ERROR_MESSAGE);
    return;
  }
  await downloadFileByUrl(fileName, url);
};

/**
 * generate file from file path
 *
 * @param {string} filePath - The path of the file to generate.
 */
export const generateFileFromFilePath = async (filePath: string) => {
  if (!filePath || filePath === FILE_EXISTED) return null;
  try {
    const rs = await fetchSignedUrlApi(filePath);
    const url = rs.data.signedUrl;
    const response = await fetch(url);
    if (!response.ok) {
      return null;
    }
    const blob = await response.blob();
    const fileName = getFileNameFromPath(filePath);
    if (!fileName) {
      toast.error(API_UNKNOWN_ERROR_MESSAGE);
      return;
    }
    const file = new File([blob], fileName, { type: blob.type });
    return file;
  } catch (error) {
    return null;
  }
};

/**
 * open new tab by link
 *
 * @param {string} url - link
 */
export const openInNewTab = (url: string): void => {
  window.open(url, "_blank");
};
